import { useMemo } from 'react';
import type { UIMessage, ActivityPart, ToolInvocationPart, TypedToolPart, MCPToolInvocationPart } from '../types/chat';
import type { UsageMetrics, TotalUsage, AgentUsage, ToolUsage } from '../types/usage';

/**
 * Aggregates usage metrics from message parts for the Usage sidebar.
 *
 * - Total: summed from root agent-complete events (ancestry.length === 1)
 * - Per-agent: from root events' usage.children records
 * - Tools: from ToolInvocationPart and TypedToolPart across all messages
 */
export function useUsageMetrics(
  messages: UIMessage[],
  toolTimings: Map<string, { startedAt: number; completedAt?: number }>,
): UsageMetrics {
  return useMemo(() => {
    // --- Total & per-agent from root agent-complete events ---
    const total: TotalUsage = {
      inputTokens: 0,
      outputTokens: 0,
      totalTokens: 0,
      llmCalls: 0,
      totalDurationMs: 0,
      messageCount: 0,
    };

    // Keyed by a case-insensitive canonical name so one agent that appears with
    // different casing across turns (e.g. "Dex" on the SSE turn, "dex" on a
    // pushed woken turn) collapses into a single row instead of two.
    const agentMap = new Map<string, {
      displayName: string;
      inputTokens: number;
      outputTokens: number;
      totalTokens: number;
      llmCalls: number;
      invocations: number;
      totalDurationMs: number;
    }>();

    for (const msg of messages) {
      for (const part of msg.parts) {
        if (part.type !== 'data-activity') continue;
        const activity = part as ActivityPart;
        if (activity.data.activityType !== 'agent-complete') continue;
        if (activity.data.ancestry.length !== 1) continue;

        const usage = activity.data.usage;
        if (!usage) continue;

        total.inputTokens += usage.inputTokens;
        total.outputTokens += usage.outputTokens;
        total.totalTokens += usage.totalTokens;
        total.llmCalls += usage.llmCalls;
        total.totalDurationMs += usage.durationMs;
        total.messageCount += 1;

        // Per-agent breakdown. The root usage is the FOLDED total (leader +
        // delegated children). Attribute each child, then attribute the
        // leader's OWN residual (total minus children) to the root source — so
        // the per-agent rows always sum to the total: every token is accounted
        // for, none double-counted.
        const addUsage = (
          name: string,
          u: { inputTokens: number; outputTokens: number; totalTokens: number; llmCalls: number; durationMs: number },
        ) => {
          const key = name.trim().toLowerCase();
          const existing = agentMap.get(key);
          if (existing) {
            existing.inputTokens += u.inputTokens;
            existing.outputTokens += u.outputTokens;
            existing.totalTokens += u.totalTokens;
            existing.llmCalls += u.llmCalls;
            existing.invocations += 1;
            existing.totalDurationMs += u.durationMs;
          } else {
            agentMap.set(key, {
              displayName: name,
              inputTokens: u.inputTokens,
              outputTokens: u.outputTokens,
              totalTokens: u.totalTokens,
              llmCalls: u.llmCalls,
              invocations: 1,
              totalDurationMs: u.durationMs,
            });
          }
        };

        const childEntries = usage.children ? Object.entries(usage.children) : [];
        if (childEntries.length > 0) {
          let cIn = 0, cOut = 0, cTotal = 0, cCalls = 0;
          for (const [name, childUsage] of childEntries) {
            addUsage(name, childUsage);
            cIn += childUsage.inputTokens;
            cOut += childUsage.outputTokens;
            cTotal += childUsage.totalTokens;
            cCalls += childUsage.llmCalls;
          }
          // Leader's own = folded total minus children (clamped at 0).
          const ownTotal = Math.max(0, usage.totalTokens - cTotal);
          const ownCalls = Math.max(0, usage.llmCalls - cCalls);
          if (ownTotal > 0 || ownCalls > 0) {
            addUsage(activity.data.source, {
              inputTokens: Math.max(0, usage.inputTokens - cIn),
              outputTokens: Math.max(0, usage.outputTokens - cOut),
              totalTokens: ownTotal,
              llmCalls: ownCalls,
              durationMs: usage.durationMs,
            });
          }
        } else {
          addUsage(activity.data.source, usage);
        }
      }
    }

    const agents: AgentUsage[] = Array.from(agentMap.values())
      .map(({ displayName, ...data }) => ({
        name: displayName,
        ...data,
        avgDurationMs: data.invocations > 0 ? Math.round(data.totalDurationMs / data.invocations) : 0,
      }))
      .sort((a, b) => b.totalTokens - a.totalTokens);

    // --- Tools from ToolInvocationPart, TypedToolPart, and MCPToolInvocationPart ---
    const toolMap = new Map<string, {
      invocations: number;
      totalDurationMs: number;
      completedCount: number;
    }>();
    // Deduplicate by toolCallId to prevent double-counting when both
    // tool-invocation and typed tool events fire for the same call
    const seenToolCallIds = new Set<string>();

    for (const msg of messages) {
      for (const part of msg.parts) {
        let toolName: string | undefined;
        let toolCallId: string | undefined;
        let durationMs = 0;

        if (part.type === 'tool-invocation') {
          const toolPart = part as ToolInvocationPart;
          toolName = toolPart.toolName;
          toolCallId = toolPart.toolCallId;
          const timing = toolTimings.get(toolCallId);
          if (timing?.startedAt && timing?.completedAt) {
            durationMs = timing.completedAt - timing.startedAt;
          }
        } else if (part.type === 'mcp-tool-invocation') {
          const mcpPart = part as MCPToolInvocationPart;
          toolName = mcpPart.toolName;
          toolCallId = mcpPart.toolCallId;
          // MCP tools carry their own timing data
          if (mcpPart.duration) {
            durationMs = mcpPart.duration;
          } else if (mcpPart.startedAt && mcpPart.completedAt) {
            durationMs = mcpPart.completedAt - mcpPart.startedAt;
          }
        } else if (
          part.type.startsWith('tool-') &&
          part.type !== 'tool-invocation' &&
          'toolCallId' in part
        ) {
          const typedPart = part as TypedToolPart;
          toolName = typedPart.toolName;
          toolCallId = typedPart.toolCallId;
          const timing = toolTimings.get(toolCallId);
          if (timing?.startedAt && timing?.completedAt) {
            durationMs = timing.completedAt - timing.startedAt;
          }
        }

        if (!toolName || !toolCallId) continue;
        if (seenToolCallIds.has(toolCallId)) continue;
        seenToolCallIds.add(toolCallId);

        const existing = toolMap.get(toolName);
        if (existing) {
          existing.invocations += 1;
          if (durationMs > 0) {
            existing.totalDurationMs += durationMs;
            existing.completedCount += 1;
          }
        } else {
          toolMap.set(toolName, {
            invocations: 1,
            totalDurationMs: durationMs,
            completedCount: durationMs > 0 ? 1 : 0,
          });
        }
      }
    }

    const tools: ToolUsage[] = Array.from(toolMap.entries())
      .map(([name, data]) => ({
        name,
        invocations: data.invocations,
        totalDurationMs: data.totalDurationMs,
        avgDurationMs: data.completedCount > 0 ? Math.round(data.totalDurationMs / data.completedCount) : 0,
        isSkillTool: name.includes('/'),
      }))
      .sort((a, b) => b.invocations - a.invocations);

    return { total, agents, tools };
  }, [messages, toolTimings.size]);
}
